import { StorageKeys } from "@origin-digital/platform-enums";
import {
  loadContentFromLocalStorage,
  loadContentFromCms,
  doFetch,
} from "./loader";
import bannerConfig from "./configurations.json";

/**
 * Compare a given time stamp with the time zone offset (example: 2018-11-20T07:30+11:00[Australia/Sydney]) with the local time
 * Return true if the given time if after the local time and false otherwise.
 * @param aedtTime: AEDT time stamp
 */

export interface CrisisBannerContent {
  start: string;
  end: string;
  type: BannerType;
  msg: string;
  heading: string;
  link: string;
  linkText: string;
  enabled: boolean | string;
}
export interface SecurityBannerContent {
  msgDefault: string;
  msgError: string;
  msgA: string;
  msgB: string;
  msgNB: string;
  enabled: boolean | string;
}
export type BannerId = StorageKeys.crisisBanner | StorageKeys.securitybanner;
export type BannerType = "warning" | "info";
export type BannerContentType = CrisisBannerContent | SecurityBannerContent;
export interface BannerInfo {
  id?: string;
  status: string;
  messageType?: string;
  content?: BannerContentType;
}

function isAfterLocalTime(aedtTime: string) {
  if (aedtTime) {
    const matchResult = aedtTime.match(/^(.*)\[.*\]$/);
    if (matchResult == null) {
      throw new Error("Invalid time");
    }
    const [, time] = matchResult;
    const aedtTimeDate = new Date(time);

    if (aedtTimeDate.getTime() > Date.now()) {
      return true;
    } else {
      return false;
    }
  } else {
    return undefined;
  }
}

function getCookie(name) {
  return document.cookie
    .split(";")
    .filter((cookieStr) => cookieStr.includes(`${name}=`))[0];
}

function getCookieVal(name) {
  const cookie = getCookie(name);
  if (cookie) {
    return cookie.substring(cookie.indexOf("=") + 1);
  } else {
    return null;
  }
}

function getAccountId() {
  const myAccCH = window.ContextHub.getStore("originmyacc");
  const accountDetails = myAccCH
    ? myAccCH.getItem("originStore.accountDetails")
    : null;
  return accountDetails ? accountDetails.accountId : null;
}

function hasContentChanged(localContent: BannerInfo, cmsContent: BannerInfo) {
  if (!cmsContent.content) {
    return true;
  }

  for (const key in localContent.content) {
    if (localContent.content[key] !== cmsContent.content[key]) {
      return true;
    }
  }

  return false;
}

async function loadContent(bannerIdentifier: BannerId): Promise<BannerInfo> {
  const localContent = loadContentFromLocalStorage(bannerIdentifier);
  const cmsContent = await loadContentFromCms(bannerIdentifier);

  if (!localContent || hasContentChanged(localContent, cmsContent)) {
    setBannerInfoInLocalStorage(bannerIdentifier, cmsContent);
    return cmsContent;
  }

  return localContent;
}

function setBannerInfoInLocalStorage(
  bannerId: BannerId,
  bannerInfo: BannerInfo
) {
  localStorage.setItem(bannerId, JSON.stringify(bannerInfo));
}

function updateLocalStorage(key: BannerId, value) {
  const sessionStr = localStorage.getItem(key);
  const sessionObj = sessionStr ? JSON.parse(sessionStr) : {};
  const newSessionObj = { ...sessionObj, ...value };
  localStorage.setItem(key, JSON.stringify(newSessionObj));
}

async function getNotification() {
  const userToken = getCookieVal("user-token");
  if (!userToken) {
    throw new Error("User token cannot be found");
  }

  const { env } = window.oetal || {};
  const { name: envName = "local" } = env || {};
  const config = bannerConfig[envName];
  return doFetch(config.notificationApi, {
    method: "GET",
    cache: "no-cache",
    headers: new Headers({
      "Content-Type": "application/json",
      Authorization: userToken,
    }),
  });
}

export {
  getAccountId,
  getCookieVal,
  getNotification,
  hasContentChanged,
  isAfterLocalTime,
  loadContent,
  loadContentFromCms,
  loadContentFromLocalStorage,
  setBannerInfoInLocalStorage,
  updateLocalStorage,
};
